import { useCallback, useEffect, useState } from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import BarChart from '@cloudscape-design/components/bar-chart';
import LineChart from '@cloudscape-design/components/line-chart';
import PieChart from '@cloudscape-design/components/pie-chart';
import { StatsResponse, getStats } from './api';
import BadgePhoto from './BadgePhoto';

interface Props {
  idToken: string;
}

// Show the day and month for a 'YYYY-MM-DD' bucket; fall back to the raw string.
function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Show the month and year for a 'YYYY-MM' bucket; fall back to the raw string.
function formatMonth(iso: string): string {
  const date = new Date(`${iso}-01T00:00:00Z`);
  if (isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Render a 0..1 ratio as a whole-percent string; em dash when there's no value.
function formatPercent(value: number | null): string {
  if (value === null || isNaN(value)) {
    return '—';
  }
  return `${Math.round(value * 100)}%`;
}

// A single headline metric. A big number is the right form for a KPI — no chart.
// `value` may be a pre-formatted string (e.g. a percentage) or a raw number.
function StatTile({ label, value, description }: { label: string; value: number | string; description?: string }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box fontSize="display-l" fontWeight="bold">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Box>
      {description && (
        <Box color="text-status-inactive" fontSize="body-s">
          {description}
        </Box>
      )}
    </div>
  );
}

export default function StatsPanel({ idToken }: Props) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStats(await getStats(idToken));
    } catch (e) {
      setStats(null);
      setError(e instanceof Error ? e.message : 'Failed to load usage statistics.');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // Client-side sorting for the per-user table. Called unconditionally (before
  // the early returns below) to satisfy the rules of hooks; falls back to an
  // empty list until the stats have loaded. The backend already returns
  // perUser sorted by connections desc, which we mirror as the default state.
  const { items: sortedUsers, collectionProps } = useCollection(stats?.perUser ?? [], {
    sorting: { defaultState: { sortingColumn: { sortingField: 'connections' }, isDescending: true } },
  });

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert type="error" header="Failed to load usage statistics" action={<Button onClick={() => void load()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const { totals, connectionsOverTime, perUser, interactionsByKind, engagementTotals, proposalRelevanceBreakdown, byMonth } = stats;

  // Months that actually scored at least one test, for the relevance trend line.
  const relevanceByMonth = byMonth.filter((m) => m.avgProposalRelevance !== null);

  // Bar chart is capped to the busiest users so alias labels stay legible; the
  // full breakdown is always available in the table below.
  const TOP_N = 12;
  const topUsers = perUser.slice(0, TOP_N);
  const hiddenUsers = perUser.length - topUsers.length;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={`Aggregated as of ${formatDateTime(stats.generatedAt)}`}
            actions={<Button iconName="refresh" onClick={() => void load()}>Refresh</Button>}
          >
            Adoption overview
          </Header>
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          <StatTile label="Connections" value={totals.connections} />
          <StatTile label="Unique users" value={totals.uniqueUsers} />
          <StatTile label="Engaged users" value={totals.engagedUsers} />
          <StatTile label="Interactions" value={totals.interactions} />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2" description="Pentest engagements that reached ASPEN, how many got scheduled, and PRT escalations.">Engagement &amp; scheduling</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <StatTile label="Engagements reached ASPEN" value={engagementTotals.engagementsReached} />
          <StatTile label="Scheduled with ASPEN" value={engagementTotals.scheduledWithAspen} />
          <StatTile label="Assigned externally" value={engagementTotals.assignedExternally} />
          <StatTile label="Escalations" value={engagementTotals.escalations} />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2" description="Engagements reached and scheduled by arrival month; escalations by the month they occurred.">By month</Header>}>
        <BarChart
          series={[
            {
              title: 'Engagements reached',
              type: 'bar',
              data: byMonth.map((m) => ({ x: m.month, y: m.engagementsReached })),
            },
            {
              title: 'Scheduled with ASPEN',
              type: 'bar',
              data: byMonth.map((m) => ({ x: m.month, y: m.scheduled })),
            },
            {
              title: 'Assigned externally',
              type: 'bar',
              data: byMonth.map((m) => ({ x: m.month, y: m.assignedExternally })),
            },
            {
              title: 'Escalations',
              type: 'bar',
              data: byMonth.map((m) => ({ x: m.month, y: m.escalations })),
            },
          ]}
          xScaleType="categorical"
          xTitle="Month"
          yTitle="Count"
          xTickFormatter={formatMonth}
          ariaLabel="Engagements, scheduling and escalations by month"
          height={300}
          hideFilter
          empty={<Box textAlign="center" color="inherit">No engagements recorded yet.</Box>}
        />
      </Container>

      <Container header={<Header variant="h2" description="For each test scheduled with ASPEN, how much of ASPEN's initial tester proposal (same tester and overlapping dates) survived into the final booking. 100% = booked exactly as proposed; dropped or added testers and date changes lower the score. Tests scheduled without a recorded proposal are excluded.">Proposal relevance</Header>}>
        <ColumnLayout columns={2}>
          <StatTile
            label="Avg. proposal relevance"
            value={formatPercent(engagementTotals.avgProposalRelevance)}
            description={`Across ${engagementTotals.proposalRelevanceScored.toLocaleString()} scheduled test${engagementTotals.proposalRelevanceScored === 1 ? '' : 's'} with a recorded proposal`}
          />
          <PieChart
            variant="donut"
            data={[
              { title: 'Accepted as proposed', value: proposalRelevanceBreakdown.acceptedStraight },
              { title: 'Partially modified', value: proposalRelevanceBreakdown.partiallyModified },
              { title: 'Fully overridden', value: proposalRelevanceBreakdown.replaced },
            ]}
            innerMetricValue={engagementTotals.proposalRelevanceScored.toLocaleString()}
            innerMetricDescription="scored tests"
            ariaLabel="Proposal outcome breakdown"
            size="medium"
            hideFilter
            empty={<Box textAlign="center" color="inherit">No scheduled tests with a recorded proposal yet.</Box>}
          />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2" description="Average proposal relevance for tests scheduled each month (months with at least one scored test).">Proposal relevance by month</Header>}>
        <LineChart
          series={[
            {
              title: 'Avg. proposal relevance',
              type: 'line',
              data: relevanceByMonth.map((m) => ({ x: m.month, y: m.avgProposalRelevance as number })),
              valueFormatter: (y) => formatPercent(y),
            },
          ]}
          xScaleType="categorical"
          yDomain={[0, 1]}
          xTitle="Month"
          yTitle="Relevance"
          xTickFormatter={formatMonth}
          yTickFormatter={(y) => formatPercent(y)}
          ariaLabel="Proposal relevance by month"
          height={280}
          hideFilter
          empty={<Box textAlign="center" color="inherit">No scored tests yet.</Box>}
        />
      </Container>

      <Container header={<Header variant="h2" description="Daily app connections and how many distinct people connected.">Connections over time</Header>}>
        <LineChart
          series={[
            {
              title: 'Connections',
              type: 'line',
              data: connectionsOverTime.map((d) => ({ x: d.date, y: d.connections })),
            },
            {
              title: 'Unique users',
              type: 'line',
              data: connectionsOverTime.map((d) => ({ x: d.date, y: d.uniqueUsers })),
            },
          ]}
          xScaleType="categorical"
          xTitle="Date"
          yTitle="Count"
          xTickFormatter={formatDay}
          ariaLabel="Connections over time"
          height={280}
          hideFilter
          empty={<Box textAlign="center" color="inherit">No connections recorded yet.</Box>}
        />
      </Container>

      <ColumnLayout columns={2}>
        <Container header={<Header variant="h2" description="Busiest users by connection count and interactions.">Top users</Header>}>
          <BarChart
            series={[
              {
                title: 'Connections',
                type: 'bar',
                data: topUsers.map((u) => ({ x: u.alias, y: u.connections })),
              },
              {
                title: 'Interactions',
                type: 'bar',
                data: topUsers.map((u) => ({ x: u.alias, y: u.interactions })),
              },
            ]}
            xScaleType="categorical"
            horizontalBars
            xTitle="User"
            yTitle="Count"
            ariaLabel="Top users by activity"
            height={320}
            hideFilter
            empty={<Box textAlign="center" color="inherit">No usage recorded yet.</Box>}
          />
          {hiddenUsers > 0 && (
            <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 's' }}>
              Showing the top {TOP_N} of {perUser.length} users. See the table below for the full list.
            </Box>
          )}
        </Container>

        <Container header={<Header variant="h2" description="How engaged users interact with the tool.">Interaction mix</Header>}>
          <PieChart
            variant="donut"
            data={[
              { title: 'Dialog messages', value: interactionsByKind.dialog },
              { title: 'Scheduling actions', value: interactionsByKind.scheduling },
            ]}
            innerMetricValue={totals.interactions.toLocaleString()}
            innerMetricDescription="interactions"
            ariaLabel="Interaction breakdown by kind"
            size="medium"
            hideFilter
            empty={<Box textAlign="center" color="inherit">No interactions recorded yet.</Box>}
          />
        </Container>
      </ColumnLayout>

      <Table
        {...collectionProps}
        header={<Header variant="h2" counter={`(${perUser.length})`}>Per-user activity</Header>}
        items={sortedUsers}
        empty="No usage recorded yet."
        columnDefinitions={[
          {
            id: 'alias',
            header: 'User',
            cell: (u) => (
              <span className="manager-cell">
                <BadgePhoto alias={u.alias} size={28} />
                {u.alias}
              </span>
            ),
            sortingField: 'alias',
          },
          { id: 'connections', header: 'Connections', cell: (u) => u.connections, sortingField: 'connections' },
          { id: 'interactions', header: 'Interactions', cell: (u) => u.interactions, sortingField: 'interactions' },
          { id: 'dialog', header: 'Dialog msgs', cell: (u) => u.dialogInteractions, sortingField: 'dialogInteractions' },
          { id: 'scheduling', header: 'Scheduling', cell: (u) => u.schedulingInteractions, sortingField: 'schedulingInteractions' },
          { id: 'firstSeen', header: 'First seen', cell: (u) => formatDateTime(u.firstSeen), sortingField: 'firstSeen' },
          { id: 'lastSeen', header: 'Last seen', cell: (u) => formatDateTime(u.lastSeen), sortingField: 'lastSeen' },
        ]}
      />
    </SpaceBetween>
  );
}
